import { Icon } from '../Icon';
import { useContentMotion } from './useContentMotion';
import { WorkspaceImage } from './WorkspaceImage';
import { plural, t } from '../../i18n/I18n';

/**
 * Ready-made projects anyone can open from the workspace — the shared link's own test
 * material. A fresh browser holds no projects at all, so without these the first screen
 * offers nothing to open; with them a tester lands in a furnished plan in one click.
 *
 * They are ordinary cloud projects opened through the same path as «Открыть по ссылке»:
 * the first open downloads and converts their models, and what the tester edits afterwards
 * is their own local copy, exactly like a project they started themselves.
 */
export interface DemoProject {
  /** Cloud project id — what `?project=` carries. */
  id: string;
  name: string;
  summary: string;
  /** Image stem under `/hp/demo`. */
  cover: string;
  floors: number;
  rooms: number;
  /** Floor area of every room on every level, m², inside the walls as the app measures it (`projectFloorArea`). */
  area: number;
  /** Said on the card when the first open is a long download. */
  note?: string;
}

export const demoProjects: DemoProject[] = [
  {
    id: '6a8461383c7b8de5bc0ce6be',
    get name() { return t('#DemoProjectTwoFloorsName'); },
    get summary() { return t('#DemoProjectTwoFloorsSummary'); },
    cover: 'house-two-floors',
    floors: 2,
    rooms: 16,
    area: 193,
  },
  {
    id: '6a9ebcec7ffb6ed5ed147d20',
    get name() { return t('#DemoProjectKitchenLivingName'); },
    get summary() { return t('#DemoProjectKitchenLivingSummary'); },
    cover: 'kitchen-living',
    floors: 1,
    rooms: 1,
    area: 82,
  },
  {
    id: '699187bc21b6517a9958ccf3',
    get name() { return t('#DemoProjectThreeFloorsName'); },
    get summary() { return t('#DemoProjectThreeFloorsSummary'); },
    cover: 'house-three-floors',
    floors: 3,
    rooms: 32,
    area: 2026,
    get note() { return t('#DemoProjectSlowNote'); },
  },
];

export const demoProject = (id: string): DemoProject | undefined => demoProjects.find((item) => item.id === id);

const floorsLabel = (count: number) => plural('#DemoProjectFloors', count);
const roomsLabel = (count: number) => plural('#DemoProjectRooms', count);

/**
 * The ready-made projects, as cards. The card opens the project straight away — the
 * editor's own loading screen reports the download and can cancel it — so nothing here
 * waits on the model catalog before responding to the click.
 */
export function DemoProjectCards({ items = demoProjects, onOpen }: {
  items?: DemoProject[]; onOpen: (id: string, name: string) => void;
}) {
  const ref = useContentMotion(items.map(item => item.id).join(':'));
  return <div ref={ref} className="demo-grid">{items.map(item =>
    <button type="button" className="demo-card" key={item.id} onClick={() => onOpen(item.id, item.name)}
      aria-label={t('#DemoProjectOpen', item.name)}>
      <span className="demo-image">
        <WorkspaceImage src={'/hp/demo/' + item.cover + '.jpg'} alt={t('#DemoProjectCoverAlt', item.name)} loading="lazy" />
        <span className="demo-floors"><Icon name="layers" size={14} />{floorsLabel(item.floors)}</span>
        <span className="image-arrow"><Icon name="arrowRight" size={20} /></span>
      </span>
      <strong>{item.name}</strong>
      <span className="demo-meta">{roomsLabel(item.rooms)}<span className="meta-dot">·</span>{t('#ValueSquareMeters', item.area)}</span>
      <span className="demo-summary">{item.summary}</span>
      {item.note && <span className="demo-note"><Icon name="info" size={15} />{item.note}</span>}
    </button>)}
  </div>;
}
